const escapeHtml = (value) =>
	String(value ?? "")
		.replace(/&/g, "&amp;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;")
		.replace(/"/g, "&quot;");

const money = (symbol, amount) => {
	const val = Number(amount);
	return `${symbol}${(Number.isFinite(val) ? val : 0).toFixed(2)}`;
};

export const TRANSACTION_PRINT_STYLES = `
@page { size: A4 portrait; margin: 10mm 12mm; }
* { box-sizing: border-box; }
body {
	font-family: Arial, Helvetica, sans-serif;
	font-size: 11px;
	color: #111;
	margin: 0;
	padding: 0;
	-webkit-print-color-adjust: exact;
	print-color-adjust: exact;
}
.receipt { width: 100%; }
.receipt-shop {
	text-align: center;
	font-size: 14px;
	font-weight: 700;
	margin: 0 0 2px;
}
.receipt-title {
	text-align: center;
	font-size: 13px;
	font-weight: 700;
	margin: 0 0 10px;
	text-transform: uppercase;
	letter-spacing: 0.04em;
}
.receipt-divider {
	border: 0;
	border-top: 1px dashed #999;
	margin: 10px 0;
}
.receipt-meta {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 4px 20px;
	margin-bottom: 10px;
	font-size: 11px;
}
.receipt-meta .label { color: #555; }
.receipt-meta .value { font-weight: 600; }
table.receipt-items {
	width: 100%;
	border-collapse: collapse;
	margin-bottom: 10px;
}
table.receipt-items th,
table.receipt-items td {
	border: 1px solid #bbb;
	padding: 5px 6px;
	vertical-align: top;
	font-size: 10px;
	line-height: 1.35;
}
table.receipt-items th {
	background: #f3f4f6;
	font-weight: 700;
	text-align: left;
}
table.receipt-items thead { display: table-header-group; }
table.receipt-items tfoot { display: table-footer-group; }
table.receipt-items tr { page-break-inside: avoid; }
.num { text-align: right; white-space: nowrap; }
.item-name { font-weight: 600; }
.item-sub { display: block; color: #555; font-size: 9px; margin-top: 1px; }
.receipt-summary-wrap { display: flex; justify-content: flex-end; margin-top: 4px; }
table.receipt-summary {
	width: 100%;
	max-width: 300px;
	border-collapse: collapse;
}
table.receipt-summary td {
	padding: 4px 0;
	font-size: 11px;
	border: 0;
}
table.receipt-summary td:last-child {
	text-align: right;
	font-weight: 600;
	white-space: nowrap;
}
table.receipt-summary tr.total td {
	border-top: 1px solid #111;
	padding-top: 6px;
	font-size: 12px;
	font-weight: 700;
}
table.receipt-summary tr.paid td:last-child { color: #0a7a32; }
table.receipt-summary tr.due td:last-child { color: #b42318; }
.receipt-note {
	margin-top: 10px;
	font-size: 10px;
	color: #555;
}
.receipt-footer {
	margin-top: 14px;
	text-align: center;
	font-size: 10px;
	color: #666;
}
`;

export const openTransactionPrintWindow = (title, bodyHtml) => {
	const printWindow = window.open("", "_blank", "width=900,height=700");
	if (!printWindow) return;

	printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
	<meta charset="utf-8" />
	<title>${escapeHtml(title)}</title>
	<style>${TRANSACTION_PRINT_STYLES}</style>
</head>
<body>${bodyHtml}</body>
</html>`);
	printWindow.document.close();
	printWindow.focus();
	window.setTimeout(() => {
		printWindow.print();
		printWindow.close();
	}, 400);
};

const summaryRow = (label, value, className = "") =>
	`<tr class="${className}"><td>${escapeHtml(label)}</td><td>${value}</td></tr>`;

export const buildPatientLedgerReceiptHtml = (payment, options = {}) => {
	const {
		pharmacyName = "",
		t = (key) => key,
		getCurrencySymbol = () => "₹",
		formatDate = (d) => d,
		calculatedSalesFooter = null,
		getTransactionTypeLabel = (type) => type,
		hasAnyBalance = () => false,
	} = options;

	const symbol = getCurrencySymbol(payment?.currency);
	const sales = payment?.sales;
	const items = sales?.items || [];

	const itemRows = items
		.map((item, index) => {
			const itemPrice = Number(
				item.unitPricePerMedicine || item.sellingPrice || item.MRP || 0
			);
			const taxPercentage =
				Number(item.taxPercentage) || Number(sales?.taxPercentage) || 0;
			const itemPriceTaxExcluded =
				itemPrice / (1 + taxPercentage / 100);
			const quantity = Number(item.medicineQunatity || 0);
			const grossTotal = itemPrice * quantity;
			const taxableAmount = grossTotal / (1 + taxPercentage / 100);
			const discountAmount =
				Number(item.discount) > 0
					? taxableAmount * (Number(item.discount) / 100)
					: 0;
			const netTaxableAmount = taxableAmount - discountAmount;
			const lineTax =
				Number(item.CGST || 0) +
				Number(item.SGST || 0) +
				Number(item.IGST || 0) ||
				Number(item.tax || 0);

			const subtitle =
				item.medicineName && item.medicineName !== item.brandName
					? `<span class="item-sub">${escapeHtml(item.medicineName)}</span>`
					: "";

			return `<tr>
				<td class="num">${index + 1}</td>
				<td><span class="item-name">${escapeHtml(item.brandName || "-")}</span>${subtitle}</td>
				<td>${escapeHtml(item.batchNumber || "-")}</td>
				<td>${escapeHtml(item.productForm || "-")}</td>
				<td class="num">${quantity}</td>
				<td class="num">${money(symbol, itemPriceTaxExcluded)}</td>
				<td class="num">${taxPercentage}%</td>
				<td class="num">${money(symbol, lineTax)}</td>
				<td class="num">${money(symbol, grossTotal)}</td>
			</tr>`;
		})
		.join("");

	const summaryRows = [];
	if (calculatedSalesFooter) {
		summaryRows.push(
			summaryRow(
				t("Total Taxable Value"),
				money(symbol, calculatedSalesFooter.subtotal)
			)
		);
		if (sales?.discount > 0) {
			summaryRows.push(
				summaryRow(
					t("Discount"),
					`-${money(symbol, calculatedSalesFooter.discountAmt)}`
				)
			);
		}
		if (sales?.transportCost > 0) {
			summaryRows.push(
				summaryRow(t("Shipping"), money(symbol, sales.transportCost))
			);
		}
		if (sales?.tax > 0) {
			summaryRows.push(
				summaryRow(
					sales?.isIGST ? t("IGST") : t("GST"),
					money(symbol, sales.tax)
				)
			);
		}
	}

	const grandTotal = sales?.netTotal ?? payment?.amount ?? 0;
	summaryRows.push(
		summaryRow(t("Grand Total"), money(symbol, grandTotal), "total")
	);
	summaryRows.push(
		summaryRow(t("Amount Paid"), money(symbol, payment?.amount || 0), "paid")
	);

	if (payment?.balances && hasAnyBalance(payment)) {
		summaryRows.push(
			summaryRow(
				t("Outstanding Balance"),
				money(
					getCurrencySymbol(payment.currency),
					payment.balances[payment.currency] || 0
				),
				"due"
			)
		);
	} else if (payment?.balances) {
		summaryRows.push(
			summaryRow(t("Payment Status"), escapeHtml(t("Fully Paid")), "paid")
		);
	}

	if (payment?.paymentType) {
		summaryRows.push(
			summaryRow(
				t("Payment Method"),
				escapeHtml(String(payment.paymentType).toUpperCase())
			)
		);
	}

	if (!summaryRows.length) {
		summaryRows.push(
			summaryRow(t("Amount"), money(symbol, payment?.amount || 0), "total")
		);
	}

	const itemsTable =
		items.length > 0
			? `<table class="receipt-items">
			<thead>
				<tr>
					<th class="num">#</th>
					<th>${escapeHtml(t("Item"))}</th>
					<th>${escapeHtml(t("Batch"))}</th>
					<th>${escapeHtml(t("Form"))}</th>
					<th class="num">${escapeHtml(t("Qty"))}</th>
					<th class="num">${escapeHtml(t("Rate"))}</th>
					<th class="num">${escapeHtml(t("Tax %"))}</th>
					<th class="num">${escapeHtml(t("Tax"))}</th>
					<th class="num">${escapeHtml(t("Amount"))}</th>
				</tr>
			</thead>
			<tbody>${itemRows}</tbody>
		</table>`
			: "";

	return `<div class="receipt">
		${pharmacyName ? `<p class="receipt-shop">${escapeHtml(pharmacyName)}</p>` : ""}
		<p class="receipt-title">${escapeHtml(t("Transaction Details"))}</p>
		<div class="receipt-meta">
			<div><span class="label">${escapeHtml(t("Patient"))}: </span><span class="value">${escapeHtml(payment?.patientName || "-")}</span></div>
			<div><span class="label">${escapeHtml(t("Date"))}: </span><span class="value">${escapeHtml(formatDate(payment?.createdAt))}</span></div>
			<div><span class="label">${escapeHtml(t("Receipt No"))}: </span><span class="value">${escapeHtml(payment?.receiptNumber || "-")}</span></div>
			${
				sales?.salesNumber
					? `<div><span class="label">${escapeHtml(t("Invoice No"))}: </span><span class="value">${escapeHtml(sales.salesNumber)}</span></div>`
					: ""
			}
			${
				sales?.salesType
					? `<div><span class="label">${escapeHtml(t("Type"))}: </span><span class="value">${escapeHtml(getTransactionTypeLabel(sales.salesType))}</span></div>`
					: ""
			}
		</div>
		<hr class="receipt-divider" />
		${itemsTable}
		<div class="receipt-summary-wrap">
			<table class="receipt-summary"><tbody>${summaryRows.join("")}</tbody></table>
		</div>
		<p class="receipt-footer">${escapeHtml(t("Thank you"))}</p>
	</div>`;
};

export const buildWalletTransactionReceiptHtml = (viewData, options = {}) => {
	const {
		pharmacyName = "",
		t = (key) => key,
		getCurrencySymbol = () => "₹",
		formatDate = (d) => d,
		getTransactionTypeLabel = (type) => type,
	} = options;

	const details = viewData?.detailedInfo?.details;
	const txDetails = viewData?.detailedInfo?.transactionDetails;
	const currencyCode =
		details?.currencyType || txDetails?.currencyType || "INR";
	const symbol = getCurrencySymbol(currencyCode);

	const docNo =
		details?.salesNumber ||
		details?.purchaseNumber ||
		details?.receiptNumber ||
		txDetails?.invoiceId ||
		"-";

	const dateValue =
		details?.createdAt ||
		details?.purchaseDate ||
		txDetails?.createdAt;

	const stocks = details?.stocks || [];
	const items = details?.items || [];
	const lineItems = stocks.length > 0 ? stocks : items;

	const itemRows = lineItems
		.map((item, index) => {
			const qty =
				item.totalMedicine ||
				item.medicineQunatity ||
				item.quantity ||
				item.totalMedicineReturned ||
				0;
			const lineTotal =
				item.totalPrice || item.subTotal || item.unitPrice || 0;
			const subtitle =
				item.medicineName && item.medicineName !== item.brandName
					? `<span class="item-sub">${escapeHtml(item.medicineName)}</span>`
					: "";

			return `<tr>
				<td class="num">${index + 1}</td>
				<td><span class="item-name">${escapeHtml(item.brandName || "-")}</span>${subtitle}</td>
				<td>${escapeHtml(item.batchNumber || "-")}</td>
				<td class="num">${escapeHtml(qty)}</td>
				<td class="num">${money(symbol, item.unitPrice)}</td>
				<td class="num">${money(symbol, item.unitPricePerMedicine || item.unitPrice)}</td>
				<td class="num">${money(symbol, lineTotal)}</td>
			</tr>`;
		})
		.join("");

	const summaryRows = [];
	if (details?.discount > 0) {
		summaryRows.push(
			summaryRow(
				t("Discount"),
				`-${details.discount}%`
			)
		);
	}
	if (details?.tax > 0) {
		const taxable = (details.netTotal || 0) - (details.tax || 0);
		summaryRows.push(
			summaryRow(t("Total Taxable Value"), money(symbol, taxable))
		);
		summaryRows.push(summaryRow(t("Tax"), money(symbol, details.tax)));
	}

	const netTotal =
		details?.netTotal ?? details?.NetTotal ?? details?.amount ?? txDetails?.amount ?? 0;
	summaryRows.push(
		summaryRow(t("Grand Total"), money(symbol, netTotal), "total")
	);

	if (details?.transportCost > 0) {
		summaryRows.push(
			summaryRow(t("Transport Cost"), money(symbol, details.transportCost))
		);
	}
	if (details?.totalPaid !== undefined) {
		summaryRows.push(
			summaryRow(t("Total Paid"), money(symbol, details.totalPaid), "paid")
		);
	}

	const balances = details?.balances
		? details.balances
		: details?.balance
			? { [currencyCode]: details.balance }
			: null;

	if (balances) {
		Object.entries(balances).forEach(([currency, balance]) => {
			if (balance > 0) {
				summaryRows.push(
					summaryRow(
						`${t("Due Amount")} (${currency})`,
						money(getCurrencySymbol(currency), balance),
						"due"
					)
				);
			}
		});
	}

	const itemsTable =
		lineItems.length > 0
			? `<table class="receipt-items">
			<thead>
				<tr>
					<th class="num">#</th>
					<th>${escapeHtml(t("Product/Brand"))}</th>
					<th>${escapeHtml(t("Batch No"))}</th>
					<th class="num">${escapeHtml(t("Qty"))}</th>
					<th class="num">${escapeHtml(t("Unit Cost"))}</th>
					<th class="num">${escapeHtml(t("Unit MRP"))}</th>
					<th class="num">${escapeHtml(t("Total"))}</th>
				</tr>
			</thead>
			<tbody>${itemRows}</tbody>
		</table>`
			: "";

	const walletOnly =
		!lineItems.length && txDetails
			? `<div class="receipt-meta">
				<div><span class="label">${escapeHtml(t("Amount"))}: </span><span class="value">${money(symbol, txDetails.amount)}</span></div>
				<div><span class="label">${escapeHtml(t("Method"))}: </span><span class="value">${escapeHtml(txDetails.transactionType || "-")}</span></div>
			</div>`
			: "";

	const reason =
		txDetails?.reason || details?.notes
			? `<p class="receipt-note"><strong>${escapeHtml(txDetails?.reason ? t("Reason") : t("Notes"))}:</strong> ${escapeHtml(txDetails?.reason || details?.notes)}</p>`
			: "";

	if (!summaryRows.length) {
		summaryRows.push(
			summaryRow(t("Amount"), money(symbol, netTotal), "total")
		);
	}

	return `<div class="receipt">
		${pharmacyName ? `<p class="receipt-shop">${escapeHtml(pharmacyName)}</p>` : ""}
		<p class="receipt-title">${escapeHtml(t("Transaction Details"))}</p>
		<div class="receipt-meta">
			<div><span class="label">${escapeHtml(t("Transaction Type"))}: </span><span class="value">${escapeHtml(getTransactionTypeLabel(viewData?.actionType))}</span></div>
			<div><span class="label">${escapeHtml(t("Date"))}: </span><span class="value">${escapeHtml(formatDate(dateValue))}</span></div>
			<div><span class="label">${escapeHtml(t("Transaction No"))}: </span><span class="value">${escapeHtml(docNo)}</span></div>
			${
				details?.patientName
					? `<div><span class="label">${escapeHtml(t("Patient"))}: </span><span class="value">${escapeHtml(details.patientName)}</span></div>`
					: ""
			}
			${
				details?.supplierName
					? `<div><span class="label">${escapeHtml(t("Supplier"))}: </span><span class="value">${escapeHtml(details.supplierName)}</span></div>`
					: ""
			}
		</div>
		<hr class="receipt-divider" />
		${walletOnly}
		${itemsTable}
		<div class="receipt-summary-wrap">
			<table class="receipt-summary"><tbody>${summaryRows.join("")}</tbody></table>
		</div>
		${reason}
		<p class="receipt-footer">${escapeHtml(t("Thank you"))}</p>
	</div>`;
};
